import { z } from 'zod';
import { APP_SLUG_PATTERN } from '../../shared/app-slug';
/**
 * Stable fixture-principal keys a golden-dataset item may run as.
 *
 * The key names a PINNED user in the eval fixture tenant, never a live account,
 * so an item's answer stays reproducible across fixture reseeds. v1 has a single
 * principal because an org manager is entitled to every installed app
 * implicitly — a seated non-manager principal needs a seat in the fixture's
 * installed-app row before authorization-negative items make sense.
 *
 * Adding a principal is an additive enum change plus a fixture user with a pinned
 * id; the runner's key→user map must stay exhaustive over this enum.
 */ export const EvalPrincipalSchema = z.enum([
    'org-manager'
]);
/** Bounded because the question is replayed as a chat turn, not stored prose. */ const MAX_EVAL_QUESTION_LENGTH = 2000;
/** The shared shape of every authored anchor list: non-empty strings, absent means "no assertion". */ const anchorList = ()=>z.array(z.string().min(1)).default([]);
/**
 * The question and the principal that asks it.
 *
 * `principal` is validated against the closed enum on purpose: an unknown key
 * must fail its item into the run's `invalid-items` score, never silently fall
 * back to a default user — a fallback would run the turn as the wrong principal
 * and score the answer as if it were the authored one.
 */ export const EvalDatasetItemInputSchema = z.strictObject({
    question: z.string().min(1).max(MAX_EVAL_QUESTION_LENGTH),
    principal: EvalPrincipalSchema
});
/**
 * A labeled value the answer must state verbatim, e.g. an aggregation total.
 *
 * Matched on DIGIT BOUNDARIES: `15` is not satisfied by `150` or `2015`, so an
 * aggregation that is off by an order of magnitude fails instead of passing on a
 * substring. Write the value exactly as the answer renders it, German decimal
 * comma included (`256,67`).
 */ export const EvalExpectedFactSchema = z.strictObject({
    label: z.string().min(1),
    value: z.string().min(1)
});
/**
 * The assertions the deterministic evaluators run against one answer.
 *
 * Strict on unknown keys: a misspelled assertion key (`mustMentions`) would
 * otherwise be stripped and the item would pass with that assertion silently
 * missing — a false green is worse than a rejected item, and prompt owners author
 * this JSON by hand in the Langfuse UI.
 *
 * The four anchor arrays default to empty so authors write only the assertions
 * an item actually makes.
 */ export const EvalDatasetItemExpectedOutputSchema = z.strictObject({
    /** Anchors the ANSWER must contain — proper names, ids, digits. */ mustMention: anchorList(),
    /**
   * Anchors NO TOOL may return — a RETRIEVAL-level assertion, not a prose one.
   *
   * The check reads the raw tool OUTPUTS, never the answer text (it posts the
   * score `must-not-surface`, which is what this field is called on the Langfuse
   * side): a correct refusal that names what it refused ("Sie haben keinen
   * Zugriff auf X") must not convict itself, and an entity that reached the
   * agent is a leak whether or not the answer repeated it.
   *
   * Consequence for authors: write what the agent must not RETRIEVE. An anchor
   * the agent is merely expected to stay silent about, while its tools legitimately
   * return it, will score 0 here — that is the check working, not a false positive.
   */ mustNotMention: anchorList(),
    /**
   * Tools that must have been consulted — each must have RETURNED a result.
   *
   * A tool that returned a domain-level failure or an empty result still counts
   * as consulted; only a call that THREW does not. The assertion is "the agent
   * looked here", and an empty answer from the right tool is still the right
   * tool (`extractToolCalls`' `status: 'success'` means "returned a result").
   */ expectedTools: anchorList(),
    /** Tools the agent must not reach for — a CALL is the violation, even a failed one. */ forbiddenTools: anchorList(),
    expectedFacts: z.array(EvalExpectedFactSchema).optional(),
    notes: z.string().optional()
});
/**
 * One golden-dataset item as prompt owners author it in the Langfuse UI (D8: the
 * Langfuse UI is the source of truth for dataset items; the repo ships only the
 * runner and the checks). The runner Zod-validates the ENTIRE dataset before
 * spending anything — malformed items are reported by the run-level
 * `invalid-items` score and are excluded from every denominator.
 *
 * The WRAPPER is deliberately non-strict: the SDK hands us a full `DatasetItem`
 * (`id`, `status`, `datasetId`, `datasetName`, `metadata`, timestamps, …), so a
 * strict wrapper would score every real item invalid. Strictness belongs on the
 * two AUTHORED payloads, where a typo silently drops an assertion.
 */ export const EvalDatasetItemSchema = z.object({
    input: EvalDatasetItemInputSchema,
    expectedOutput: EvalDatasetItemExpectedOutputSchema
});
/**
 * Which ADAPTER a task key addresses (D6: ONE endpoint, one task key per
 * dataset). The family is the code axis — an entry here is a new adapter in the
 * runner's registry — while WHICH app a run benchmarks is data inside the key,
 * so putting a second app chat under eval is a dataset trigger and a manifest,
 * never a change to core.
 */ export const EvalTaskFamilySchema = z.enum([
    'app-chat'
]);
/**
 * A task key: `app-chat:<appId>:<kind>`, e.g. `app-chat:omniscient:omniscient`.
 *
 * The key is also the single-active-run LOCK key, so carrying the target inside
 * it is what lets two apps benchmark concurrently while one app's chat kind
 * still admits only one run at a time.
 */ export const EVAL_TASK_KEY_PATTERN = new RegExp(`^(${EvalTaskFamilySchema.options.join('|')}):(${APP_SLUG_PATTERN}):(${APP_SLUG_PATTERN})$`);
/** Bounded because the key is externally supplied, then indexed and logged. */ const MAX_EVAL_TASK_KEY_LENGTH = 128;
export const EvalTaskKeySchema = z.string().max(MAX_EVAL_TASK_KEY_LENGTH).regex(EVAL_TASK_KEY_PATTERN, 'An eval task key looks like "app-chat:<appId>:<kind>"');
/**
 * Splits a VALIDATED key. Throws on anything else rather than answering with a
 * half-parsed target: every caller reaches this through `EvalTaskKeySchema`, and
 * a silent fallback would run a benchmark against an app nobody named.
 */ export function parseEvalTaskKey(key) {
    const match = EVAL_TASK_KEY_PATTERN.exec(key);
    if (!match) {
        throw new Error(`"${key}" is not an eval task key`);
    }
    const [, family, appId, kind] = match;
    return {
        family: family,
        appId,
        kind
    };
}
/**
 * The lifecycle of one durable eval run (plan 021 §4.3).
 *
 * `interrupted` and `rejected` are deliberately NOT collapsed into `failed`:
 * - `interrupted` means the run stopped REPORTING (a deploy mid-run, a crash) —
 *   the boot reaper's guess, never a diagnosis of the work itself;
 * - `rejected` means the run never started, because another run of the same task
 *   already held the single-active-run lock. It is recorded rather than dropped
 *   so a prompt owner who pressed Run and saw nothing can see why.
 *
 * A run is ACTIVE while `queued` or `running`, and exactly one active run may
 * exist per task key.
 */ export const EvalRunStatusSchema = z.enum([
    'queued',
    'running',
    'completed',
    'failed',
    'interrupted',
    'rejected'
]);
/**
 * Who asked for the run: the Langfuse remote-experiment webhook, or the
 * developer CLI fallback. Recorded because the two reach the same run path with
 * different trust and different retry behavior — only the webhook retries, which
 * is what makes trigger dedupe necessary at all.
 */ export const EvalRunTriggerSourceSchema = z.enum([
    'webhook',
    'cli'
]);
/**
 * Run parameters the prompt owner sets on the dataset's remote-experiment
 * trigger. `promptVersion` pins the prompt under test so an unlabeled draft can
 * be benchmarked before any label moves; omitted means "resolve the `dev` label
 * once and record what it resolved to".
 *
 * `task` is a plain optional STRING here, not `EvalTaskKeySchema`, even though
 * the runner requires a valid key: a trigger configured with a missing or
 * misspelled task must come back as a readable endpoint error that names both
 * what arrived and the keys that exist, not as a raw Zod issue on a payload the
 * prompt owner never sees. The runner's task registry is what validates it, and
 * its message is what the ACK carries.
 */ export const EvalTriggerConfigSchema = z.looseObject({
    task: z.string().min(1).optional(),
    promptVersion: z.number().int().positive().optional()
});
/**
 * The payload Langfuse POSTs when an owner presses Run on a dataset's remote
 * experiment.
 *
 * DELIBERATELY LENIENT: this shape is reconstructed from the Langfuse docs, not
 * from an observed request. The trigger-endpoint work captures the real headers
 * and body (plan 021 §4.5 prerequisite 1) and tightens this schema then — until
 * that capture lands, an unexpected field must not reject the trigger.
 */ export const EvalTriggerRequestSchema = z.looseObject({
    datasetId: z.string().min(1).optional(),
    datasetName: z.string().min(1).optional(),
    runName: z.string().min(1).optional(),
    /**
   * The trigger's own id, which makes a webhook RETRY idempotent: the endpoint
   * answers a repeated id with the run it already started instead of starting a
   * second one.
   *
   * CAPPED at the length the `EvalRun` storage schema accepts. Without the cap
   * an oversized id reaches Mongoose's `maxlength` validator and surfaces as a
   * 500 on a payload the trigger owner can actually fix — the endpoint has to
   * say so in a 400 instead.
   */ triggerId: z.string().min(1).max(256).optional(),
    config: EvalTriggerConfigSchema.optional()
});
/**
 * What the trigger endpoint ACKs with, within the 10 s Langfuse allows — the
 * run's identity and what happened to it, never its results (the run has not
 * executed an item yet when this is written).
 *
 * `status` is the RUN's status, not the acquisition outcome: a replayed trigger
 * comes back as the run it already produced, which may be `rejected`,
 * `completed` or `interrupted`. Only `queued` means "items are about to run".
 *
 * `reason` is present when the run will not produce results (a rejected run
 * naming the run that holds the lock). It is the only place the person who
 * pressed Run learns why nothing happened before the Langfuse UI shows a
 * dataset run that never appears.
 */ export const EvalTriggerResponseSchema = z.strictObject({
    runId: z.string().min(1),
    runName: z.string().min(1),
    status: EvalRunStatusSchema,
    reason: z.string().optional()
});
/**
 * Everything that has to match for two runs to be comparable, recorded as run
 * metadata. Two runs may be compared ONLY when their manifests are identical on
 * every field but `resolvedPromptVersion` — otherwise a metric move can come from
 * a reseeded fixture, a re-embedded index, or a model swap rather than the prompt.
 *
 * `embeddingGeneration` is a string because the backend's `embeddingGeneration()`
 * composes deployment, dimensions, and text-builder version into one key.
 *
 * STRICT — and Mongoose gives every subdocument an `_id` by default, which this
 * schema rejects. The `EvalRun` storage schema (plan 021 §4.3) must declare the
 * manifest subdoc with `{ _id: false }` (or parse an explicit field projection)
 * so a stored manifest re-parses.
 */ export const EvalRunManifestSchema = z.strictObject({
    datasetDigest: z.string().min(1),
    // A zero-item run is never comparable to anything.
    itemIds: z.array(z.string().min(1)).min(1),
    fixtureDigest: z.string().min(1),
    embeddingGeneration: z.string().min(1),
    promptName: z.string().min(1),
    resolvedPromptVersion: z.number().int().positive(),
    promptConfigDigest: z.string().min(1),
    codeSha: z.string().min(1),
    modelDeployment: z.string().min(1),
    // Optional: manifests stored before the LLM judge existed carry neither.
    judgeModel: z.string().min(1).optional(),
    /** The `decision/eval-judge` version the thresholds came from — absent when it carries none; `bundled` for the synced fallback. */ judgeThresholdsVersion: z.string().min(1).optional()
});

//# sourceMappingURL=eval-runner.schema.js.map